import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { getExclusiveTranslationKey } from "@/utils/exclusive-mapping";

import { SearchService } from "../services/search.service";

export interface PropertyOption {
  key: string;
  label: string;
}

export interface PropertyOptionsData {
  propertyClasses: PropertyOption[];
  constructionStatuses: PropertyOption[];
  heatingTypes: PropertyOption[];
  wallMaterials: PropertyOption[];
  insulationTypes: PropertyOption[];
  conditions: PropertyOption[];
  renovationTypes: PropertyOption[];
  parkingTypes: PropertyOption[];
  elevatorTypes: PropertyOption[];
  frameTypes: PropertyOption[];
  layoutStatuses: PropertyOption[];
  completionYears: PropertyOption[];
  completionQuarters: PropertyOption[];
  financeOptions: PropertyOption[];
  callStatuses: PropertyOption[];
  listingTags: PropertyOption[];
  exclusiveTypes: PropertyOption[];
  publicationStatuses: PropertyOption[];
}

/**
 * Hook for working with property options from server
 * @param namespace - translation namespace, default is "PropertyOptions"
 * @returns object with all property options data
 */
export const usePropertyOptions = (namespace = "PropertyOptions") => {
  const t = useTranslations(namespace);

  // Property options data request from server
  const {
    data: serverData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["getPropertyOptions"],
    queryFn: () => SearchService.getPropertyOptions(),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Convert server data to translated format
  const propertyOptions = useMemo<PropertyOptionsData | null>(() => {
    if (!serverData) {
      return null;
    }

    const translateOptions = (options: PropertyOption[]) =>
      options.map((option) => ({
        key: option.key,
        label: t(option.key, { fallback: option.label }),
      }));

    const translateExclusiveOptions = (options: PropertyOption[]) =>
      options.map((option) => {
        // Use getExclusiveTranslationKey to ensure we use correct keys
        const mappedKey = getExclusiveTranslationKey(option.key);
        return {
          key: option.key, // Keep original key for backend compatibility
          label: t(mappedKey, { fallback: option.label }),
        };
      });

    return {
      propertyClasses: translateOptions(serverData.propertyClasses || []),
      constructionStatuses: translateOptions(
        serverData.constructionStatuses || [],
      ),
      heatingTypes: translateOptions(serverData.heatingTypes || []),
      wallMaterials: translateOptions(serverData.wallMaterials || []),
      insulationTypes: translateOptions(serverData.insulationTypes || []),
      conditions: translateOptions(serverData.conditions || []),
      renovationTypes: translateOptions(serverData.renovationTypes || []),
      parkingTypes: translateOptions(serverData.parkingTypes || []),
      elevatorTypes: translateOptions(serverData.elevatorTypes || []),
      frameTypes: translateOptions(serverData.frameTypes || []),
      layoutStatuses: translateOptions(serverData.layoutStatuses || []),
      completionYears: translateOptions(serverData.completionYears || []),
      completionQuarters: translateOptions(serverData.completionQuarters || []),
      financeOptions: translateOptions(serverData.financeOptions || []),
      callStatuses: translateOptions(serverData.callStatuses || []),
      listingTags: translateOptions(serverData.listingTags || []),
      exclusiveTypes: translateExclusiveOptions(
        serverData.exclusiveTypes || [],
      ),
      publicationStatuses: translateOptions(
        serverData.publicationStatuses || [],
      ),
    };
  }, [serverData, t]);

  // Helper functions to get specific option groups
  const getPropertyClasses = () => propertyOptions?.propertyClasses || [];
  const getConstructionStatuses = () =>
    propertyOptions?.constructionStatuses || [];
  const getHeatingTypes = () => propertyOptions?.heatingTypes || [];
  const getWallMaterials = () => propertyOptions?.wallMaterials || [];
  const getInsulationTypes = () => propertyOptions?.insulationTypes || [];
  const getConditions = () => propertyOptions?.conditions || [];
  const getRenovationTypes = () => propertyOptions?.renovationTypes || [];
  const getParkingTypes = () => propertyOptions?.parkingTypes || [];
  const getElevatorTypes = () => propertyOptions?.elevatorTypes || [];
  const getFrameTypes = () => propertyOptions?.frameTypes || [];
  const getLayoutStatuses = () => propertyOptions?.layoutStatuses || [];
  const getCompletionYears = () => propertyOptions?.completionYears || [];
  const getCompletionQuarters = () => propertyOptions?.completionQuarters || [];
  const getFinanceOptions = () => propertyOptions?.financeOptions || [];
  const getCallStatuses = () => propertyOptions?.callStatuses || [];
  const getListingTags = () => propertyOptions?.listingTags || [];
  const getExclusiveTypes = () => propertyOptions?.exclusiveTypes || [];
  const getPublicationStatuses = () =>
    propertyOptions?.publicationStatuses || [];

  return {
    propertyOptions,
    isLoading,
    error,
    refetch,
    // Helper functions
    getPropertyClasses,
    getConstructionStatuses,
    getHeatingTypes,
    getWallMaterials,
    getInsulationTypes,
    getConditions,
    getRenovationTypes,
    getParkingTypes,
    getElevatorTypes,
    getFrameTypes,
    getLayoutStatuses,
    getCompletionYears,
    getCompletionQuarters,
    getFinanceOptions,
    getCallStatuses,
    getListingTags,
    getExclusiveTypes,
    getPublicationStatuses,
  };
};
